<template>
  <div
    v-on-click-outside="onClickOutsideHandler"
    class="wrapper flex w-full flex-col justify-between rounded-sm p-2 select-none"
  >
    <div class="flex w-full items-center justify-between text-xs font-semibold">
      <span class="pt-0.5 uppercase">
        {{ $t("ORGANIZATION.DO_CONDITIONAL_ARG", { num: argumentNum || 1 }) }}
      </span>
      <div class="relative flex items-center space-x-1">
        <span
          v-if="!isActiveTemplatePublished"
          class="mr-3 cursor-pointer text-lg"
          data-cy="add-attribute-to-condition-button"
          @click="showAddAttributeDropdown = !showAddAttributeDropdown"
        >
          +
        </span>
        <add-attribute-dropdown
          v-if="showAddAttributeDropdown"
          class="top-full right-4 z-20"
          :attributes="selectedAttributes"
          @save="handleSaveAttributes"
        />
        <icon-base
          v-if="!!argumentNum && !isActiveTemplatePublished"
          :icon="IconDelete"
          class="cursor-pointer text-disabled-medium"
          @click="handleSaveAttributes([])"
        />
      </div>
    </div>
    <div
      v-if="!isActiveTemplatePublished"
      class="flex h-14 w-full cursor-pointer items-center justify-center rounded-sm border-2 border-dashed border-primary bg-blue-200/60"
      @click="showAddAttributeDropdown = !showAddAttributeDropdown"
    >
      <span class="text-xs font-semibold text-blue-600 uppercase">
        {{ $t("ORGANIZATION.DO_ADD_ATTR").toLocaleUpperCase() }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { useDoActiveTemplateStatus } from "@/hooks/orchestration";

import AddAttributeDropdown from "./AddAttributeDropdown.vue";
import IconDelete from "@/components/icons/IconDelete.vue";

import type { IStep, IStepAttribute } from "@/models/orchestration";
import type { PropType } from "vue";

const props = defineProps({
  step: {
    type: Object as PropType<IStep | null>,
    required: true,
  },
  argumentNum: {
    type: Number as PropType<number | null>,
    default: null,
  },
});

const emit = defineEmits<{
  (eventName: "save", attributes: IStepAttribute[]): void;
}>();

const { isActiveTemplatePublished } = useDoActiveTemplateStatus();

const showAddAttributeDropdown = ref(false);

const selectedAttributes = computed(
  () =>
    props.step?.attributes?.map((attribute) => ({
      id: attribute.id,
      conditions: [],
      ...(attribute.context && { context: attribute.context }),
    })) || []
);

const handleSaveAttributes = (attributes: IStepAttribute[]) => {
  showAddAttributeDropdown.value = false;
  emit("save", attributes);
};

const onClickOutsideHandler = [
  () => (showAddAttributeDropdown.value = false),
  { capture: false },
];
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.wrapper {
  min-height: 115px;
  border-width: 3px;
  border-color: rgba(55, 105, 214, 0.15);
}
</style>
